// Per-date расчёт доступности букетов с учётом «окон» запаса (текущий сток + плановые
// поставки) и занятости корзиной. Заказ уезжает на ОДНУ дату (выбирается на чекауте).
//
// Правила запаса различаются:
//  - БУКЕТЫ: `supplyOnDate` — СУММА free всех окон, накрывающих дату (физический
//    максимум). Флористу разрешено мешать партии одного компонента — это нормально
//    для цветов одного сорта. Совпадает с admin-матрицей.
//  - СТЕБЛИ: `supplyFirstBatchOnDate` — max из {текущее окно, ПЕРВАЯ плановая по дате}.
//    Заказ стеблей берётся из ОДНОЙ партии (не смешиваем). Используется в reconcileCart
//    для стеблей и в `firstPlannedAvailable` (карточка/хидрация/addToCart).

import { todayShop } from './timezone';

function pad(n) { return String(n).padStart(2, '0'); }

function toISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Список ISO-дат [сегодня .. сегодня+horizon) в поясе магазина. */
export function horizonDates(horizon = 14) {
  const base = todayShop();
  const out = [];
  for (let i = 0; i < horizon; i++) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
    out.push(toISO(d));
  }
  return out;
}

/** ISO-дата + N дней. */
function addDaysISO(iso, days) {
  const d = new Date(iso + 'T00:00:00');
  return toISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
}

/** Дата (ISO) попадает в окно [from, until)? until=null → бессрочно. */
function dateInWindow(dateISO, win) {
  return win.from <= dateISO && (win.until == null || dateISO < win.until);
}

/**
 * Первая плановая поставка ПО ДАТЕ открытия — её доступное количество. Под правилом
 * «лимит из текущего ИЛИ первой плановой» это значение участвует во всех расчётах
 * стеблей (карточка, корзина, хидрация, реконсиляция). Поздние большие плановые НЕ
 * увеличивают лимит.
 */
export function firstPlannedAvailable(future_stock) {
  if (!future_stock || !future_stock.length) return 0;
  const sorted = future_stock.slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return sorted[0].available || 0;
}

/**
 * Строит окна запаса для продукта (стебля/компонента) из ответа /products/by-ids:
 * текущий сток (quantity, окно по stock_arrival_date+shelf) + плановые (future_stock).
 * Тот же формат, что supply_windows из каталога букетов.
 */
export function buildProductWindows(product) {
  const windows = [];
  // Пустой срок жизни в карточке = бессрочно (until=null), как на сервере (stock_utils.shelf_days)
  const shelf = product.shelf_life_days > 0 ? product.shelf_life_days : null;
  const today = toISO(todayShop());
  const free = product.quantity || 0;
  if (free > 0) {
    if (product.stock_arrival_date) {
      const arrival = product.stock_arrival_date;
      const until = shelf ? addDaysISO(arrival, shelf) : null;
      const from = arrival > today ? arrival : today;
      if (until == null || from < until) windows.push({ from, until, free, kind: 'current' });
    } else {
      windows.push({ from: today, until: null, free, kind: 'current' });
    }
  }
  (product.future_stock || []).forEach(fs => {
    if ((fs.available || 0) > 0 && fs.date) {
      windows.push({ from: fs.date, until: shelf ? addDaysISO(fs.date, shelf) : null, free: fs.available, kind: 'planned' });
    }
  });
  return windows;
}

/** [БУКЕТЫ] Запас продукта на дату — СУММА free всех окон, накрывающих дату.
 *  Физический максимум: компоненты букета можно собирать из нескольких партий
 *  одного цветка (одна и та же гвоздика из старой и новой поставки). Совпадает
 *  с admin-матрицей. */
export function supplyOnDate(windows, dateISO) {
  if (!windows || !windows.length) return 0;
  let s = 0;
  for (const w of windows) if (dateInWindow(dateISO, w)) s += w.free || 0;
  return s;
}

/** [СТЕБЛИ] Запас на дату — MAX free среди {текущее окно, ПЕРВАЯ плановая по дате}.
 *  Правило «одна партия на заказ стеблей»: клиент берёт цельную партию, без смешения.
 *  Поздние плановые в лимит не входят (даже если они больше). */
export function supplyFirstBatchOnDate(windows, dateISO) {
  if (!windows || !windows.length) return 0;
  const current = windows.find(w => w.kind === 'current');
  const firstPlanned = windows
    .filter(w => w.kind === 'planned')
    .sort((a, b) => a.from.localeCompare(b.from))[0];
  const considered = [current, firstPlanned].filter(Boolean);
  let best = 0;
  for (const w of considered) if (dateInWindow(dateISO, w)) {
    const f = w.free || 0;
    if (f > best) best = f;
  }
  return best;
}

/** Сколько физических (собранных) экземпляров букета доступно на дату.
 * Считаем по ПАРТИЯМ (bouquet.lots): суммируем доступное (available) тех партий,
 * чьё окно [assembled_at, +shelf) накрывает дату. Истёкшие партии не считаются.
 * Фолбэк на агрегатное зеркало (physical_quantity/stock_arrival_date) — для старых данных без lots. */
function physicalAvailableOnDate(bouquet, dateISO) {
  const lots = bouquet.lots;
  if (Array.isArray(lots) && lots.length) {
    let total = 0;
    for (const lot of lots) {
      const avail = lot.available != null ? lot.available : (lot.quantity || 0);
      if (avail <= 0) continue;
      const arr = lot.assembled_at;
      if (!arr || !lot.shelf_life_days) { total += avail; continue; }
      const a = new Date(arr + 'T00:00:00');
      const until = toISO(new Date(a.getFullYear(), a.getMonth(), a.getDate() + lot.shelf_life_days));
      if (arr <= dateISO && dateISO < until) total += avail;
    }
    return total;
  }
  const phys = bouquet.physical_quantity || 0;
  if (phys <= 0) return 0;
  if (!bouquet.stock_arrival_date || !bouquet.shelf_life_days) return phys;
  const arrival = bouquet.stock_arrival_date;
  const a = new Date(arrival + 'T00:00:00');
  const until = toISO(new Date(a.getFullYear(), a.getMonth(), a.getDate() + bouquet.shelf_life_days));
  if (arrival <= dateISO && dateISO < until) return phys;
  return 0;
}

/**
 * Спрос на retail-pool от всех позиций корзины КРОМЕ excludeIndex (date-независим —
 * recipe-часть; собранные экземпляры залочены в BouquetReservation и pool не трогают).
 * Возвращает { product_id: суммарное количество }.
 */
export function cartPoolDemand(cart, bouquets, excludeIndex = -1) {
  const demand = {};
  (cart || []).forEach((item, i) => {
    if (i === excludeIndex) return;
    const qty = item.quantity || 0;
    if (qty <= 0) return;
    if (item.kind === 'bouquet') {
      const b = bouquets.find(x => x.id === (item.bouquet_id || item.id));
      if (!b || !b.components) return;
      // Собранные экземпляры не берут из pool — вычитаем их из нагрузки.
      const recipeUnits = Math.max(0, qty - (b.physical_available_today || 0));
      if (recipeUnits <= 0) return;
      b.components.forEach(c => {
        demand[c.product_id] = (demand[c.product_id] || 0) + c.quantity * recipeUnits;
      });
    } else {
      const pid = item.product_id || item.id;
      demand[pid] = (demand[pid] || 0) + qty;
    }
  });
  return demand;
}

/**
 * Доступность букета на конкретную дату с учётом спроса других позиций.
 * = physical (в окне) + min по компонентам floor((supply − спрос_других) / qty).
 */
export function bouquetAvailableOnDate(bouquet, supplyWindows, demandOthers, dateISO) {
  const physAvail = physicalAvailableOnDate(bouquet, dateISO);
  // assembled_only: продаём ТОЛЬКО собранные экземпляры — recipe-доступность не добавляем.
  if (bouquet.assembled_only) return physAvail;
  if (!bouquet.components || bouquet.components.length === 0) return physAvail;
  let recipe = Infinity;
  for (const c of bouquet.components) {
    const supply = supplyOnDate(supplyWindows ? supplyWindows[c.product_id] : null, dateISO);
    const used = demandOthers[c.product_id] || 0;
    const canHave = Math.floor((supply - used) / c.quantity);
    if (canHave < recipe) recipe = canHave;
  }
  if (recipe === Infinity) recipe = 0;
  return physAvail + Math.max(0, recipe);
}

/**
 * Профиль доступности букета по горизонту при заданном спросе других позиций (map pid→qty).
 * Возвращает:
 *  - today — доступность на сегодня (инфо-бейдж «В наличии сегодня N»),
 *  - todayRaw — доступность сегодня БЕЗ учёта корзины (отличить «занято корзиной» от разрыва),
 *  - futureDate/futureCapacity — САМАЯ РАННЯЯ дата (после сегодня), где доступность больше
 *    сегодняшней, и её вместимость (инфо-бейдж «с DATE: M»),
 *  - limit — лимит «+» = вместимость на самой ранней доступной дате (max(today, futureCapacity)).
 *    Намеренно НЕ максимум по горизонту: ограничиваем ближайшей датой поставки.
 */
export function availabilityProfile(bouquet, supplyWindows, demandOthers = {}, horizon = 14) {
  const dates = horizonDates(horizon);
  const today = bouquetAvailableOnDate(bouquet, supplyWindows, demandOthers, dates[0]);
  let futureDate = null;
  let futureCapacity = 0;
  for (let i = 1; i < dates.length; i++) {
    const a = bouquetAvailableOnDate(bouquet, supplyWindows, demandOthers, dates[i]);
    if (a > today) { futureDate = dates[i]; futureCapacity = a; break; }  // самая ранняя улучшающая
  }
  const todayRaw = bouquetAvailableOnDate(bouquet, supplyWindows, {}, dates[0]);
  return {
    today,
    todayRaw,
    futureDate,
    futureCapacity,
    limit: Math.max(today, futureCapacity),
  };
}

/** Профиль для карточки: спрос других позиций берётся из корзины (кроме selfIndex). */
export function maxAvailableOverHorizon(bouquet, supplyWindows, cart, bouquets, selfIndex = -1, horizon = 14) {
  const demandOthers = cartPoolDemand(cart, bouquets, selfIndex);
  return availabilityProfile(bouquet, supplyWindows, demandOthers, horizon);
}
